import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { PlusIcon, CurrencyRupeeIcon, TruckIcon, ShoppingBagIcon, UserIcon, CalendarIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { collection, getDocs, query, orderBy, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import DataTable from '../common/DataTable';
import AddExpenseModal from './AddExpenseModal';
import StatCard from '../common/StatCard';

// Add DatePickerInput component
const DatePickerInput = React.forwardRef(({ value, onClick }, ref) => (
  <button
    onClick={onClick}
    ref={ref}
    type="button"
    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
  >
    <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" aria-hidden="true" />
    {value || 'Select date'}
  </button>
));

export default function CostsTab() {
  const [expenses, setExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalExpensesPeriod, setTotalExpensesPeriod] = useState('month');
  const [fuelExpensesPeriod, setFuelExpensesPeriod] = useState('month');
  const [foodExpensesPeriod, setFoodExpensesPeriod] = useState('month');
  const [wagesExpensesPeriod, setWagesExpensesPeriod] = useState('month');
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [periodTotalExpenses, setPeriodTotalExpenses] = useState(0);
  const [periodFuelExpenses, setPeriodFuelExpenses] = useState(0);
  const [periodFoodExpenses, setPeriodFoodExpenses] = useState(0);
  const [periodWagesExpenses, setPeriodWagesExpenses] = useState(0);
  
  const expenseCategories = [
    { id: 'fuel', name: 'Fuel Costs' },
    { id: 'food', name: 'Food Expenses' },
    { id: 'wages', name: 'Driver Wages' },
    { id: 'misc', name: 'Miscellaneous Costs' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch vehicles
        const vehiclesRef = collection(db, 'vehicles');
        const vehiclesSnap = await getDocs(vehiclesRef);
        const vehiclesList = vehiclesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setVehicles(vehiclesList);

        // Fetch expenses
        const expensesRef = collection(db, 'expenses');
        const expensesQuery = query(expensesRef, orderBy('date', 'desc'));
        const expensesSnap = await getDocs(expensesQuery);
        const expensesList = expensesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setExpenses(expensesList);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Add date filtering effect
  useEffect(() => {
    if (!dateRange.startDate && !dateRange.endDate) {
      setFilteredExpenses(expenses);
      return;
    }

    const filtered = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      expenseDate.setHours(0, 0, 0, 0);
      
      if (dateRange.startDate && dateRange.endDate) {
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return expenseDate >= start && expenseDate <= end;
      }
      
      if (dateRange.startDate) {
        const start = new Date(dateRange.startDate);
        start.setHours(0, 0, 0, 0);
        return expenseDate >= start;
      }
      
      if (dateRange.endDate) {
        const end = new Date(dateRange.endDate);
        end.setHours(23, 59, 59, 999);
        return expenseDate <= end;
      }
      
      return true;
    });
    
    setFilteredExpenses(filtered);
  }, [dateRange, expenses]);

  // Calculate totals for stats cards
  const totalExpenses = expenses.reduce((sum, exp) => sum + Number(exp.amount), 0);
  const fuelExpenses = expenses
    .filter(exp => exp.category === 'fuel')
    .reduce((sum, exp) => sum + Number(exp.amount), 0);
  const foodExpenses = expenses
    .filter(exp => exp.category === 'food')
    .reduce((sum, exp) => sum + Number(exp.amount), 0);
  const wagesExpenses = expenses
    .filter(exp => exp.category === 'wages')
    .reduce((sum, exp) => sum + Number(exp.amount), 0);

  const columns = [
    {
      header: 'Date',
      accessorKey: 'date',
      cell: info => new Date(info.getValue()).toLocaleDateString('en-GB'),
    },
    {
      header: 'Vehicle',
      accessorKey: 'vehicleRegistration',
      cell: info => (
        <div>
          <div className="font-medium">{info.getValue()}</div>
          <div className="text-sm text-gray-500">{info.row.original.driverName}</div>
        </div>
      ),
    },
    {
      header: 'Category',
      accessorKey: 'category',
      cell: info => {
        const category = expenseCategories.find(cat => cat.id === info.getValue());
        return category?.name || info.getValue();
      },
    },
    {
      header: 'Amount',
      accessorKey: 'amount',
      cell: info => `₹${parseFloat(info.getValue()).toFixed(2)}`,
    },
    {
      header: 'Notes',
      accessorKey: 'notes',
      cell: info => info.getValue() || '-',
    },
  ];

  const calculatePeriodStats = (period, expenseType) => {
    const now = new Date();
    let startDate = new Date();
    
    switch (period) {
      case 'day':
        startDate.setHours(0, 0, 0, 0);
        now.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        now.setHours(23, 59, 59, 999);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        startDate.setHours(0, 0, 0, 0);
        now.setHours(23, 59, 59, 999);
        break;
      default:
        break;
    }

    const periodExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate >= startDate && expenseDate <= now;
    });

    const amount = periodExpenses
      .filter(exp => expenseType === 'total' ? true : exp.category === expenseType)
      .reduce((sum, exp) => sum + Number(exp.amount), 0);

    switch (expenseType) {
      case 'total':
        setTotalExpensesPeriod(period);
        setPeriodTotalExpenses(amount);
        break;
      case 'fuel':
        setFuelExpensesPeriod(period);
        setPeriodFuelExpenses(amount);
        break;
      case 'food':
        setFoodExpensesPeriod(period);
        setPeriodFoodExpenses(amount);
        break;
      case 'wages':
        setWagesExpensesPeriod(period);
        setPeriodWagesExpenses(amount);
        break;
    }
  };

  const stats_cards = [
    {
      title: 'Total Expenses',
      value: `₹${(periodTotalExpenses || totalExpenses).toLocaleString()}`,
      icon: CurrencyRupeeIcon,
      bgColor: 'bg-primary-500',
      borderColor: 'border-primary-500',
      hoverBgColor: 'hover:bg-primary-500',
      textColor: 'text-primary-500',
      periodType: 'total',
      currentPeriod: totalExpensesPeriod
    },
    {
      title: 'Fuel Expenses',
      value: `₹${(periodFuelExpenses || fuelExpenses).toLocaleString()}`,
      icon: TruckIcon,
      bgColor: 'bg-green-500',
      borderColor: 'border-green-500',
      hoverBgColor: 'hover:bg-green-500',
      textColor: 'text-green-500',
      periodType: 'fuel',
      currentPeriod: fuelExpensesPeriod
    },
    {
      title: 'Food Expenses',
      value: `₹${(periodFoodExpenses || foodExpenses).toLocaleString()}`,
      icon: ShoppingBagIcon,
      bgColor: 'bg-yellow-500',
      borderColor: 'border-yellow-500',
      hoverBgColor: 'hover:bg-yellow-500',
      textColor: 'text-yellow-500',
      periodType: 'food',
      currentPeriod: foodExpensesPeriod
    },
    {
      title: 'Driver Wages',
      value: `₹${(periodWagesExpenses || wagesExpenses).toLocaleString()}`,
      icon: UserIcon,
      bgColor: 'bg-red-500',
      borderColor: 'border-red-500',
      hoverBgColor: 'hover:bg-red-500',
      textColor: 'text-red-500',
      periodType: 'wages',
      currentPeriod: wagesExpensesPeriod
    }
  ];

  const handleAddExpense = async (formData) => {
    try {
      const expenseRef = collection(db, 'expenses');
      const vehicle = vehicles.find(v => v.id === formData.vehicleId);
      const newExpense = {
        ...formData,
        vehicleRegistration: vehicle.registrationNumber,
        driverName: vehicle.driverName,
        date: new Date(formData.date).toISOString()
      };
      await addDoc(expenseRef, newExpense);
      
      // Update local state
      setExpenses(prev => [newExpense, ...prev]);
    } catch (error) {
      console.error('Error adding expense:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats_cards.map((stat) => (
          <StatCard
            key={stat.title}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            iconBgColor={stat.bgColor}
            borderColor={stat.borderColor}
            hoverBgColor={stat.hoverBgColor}
            textColor={stat.textColor}
            showPeriodToggle={true}
            periodType={stat.periodType}
            currentPeriod={stat.currentPeriod}
            onPeriodChange={calculatePeriodStats}
          />
        ))}
      </div>

      {/* Expenses Table */}
      <div className="bg-white dark:bg-[#2E3B4E] shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <div className="relative rounded-md flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                value={globalFilter ?? ''}
                onChange={e => setGlobalFilter(e.target.value)}
                placeholder="Search expenses..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <DatePicker
                selectsRange={true}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={(update) => {
                  setDateRange({ startDate: update[0], endDate: update[1] });
                }}
                customInput={<DatePickerInput />}
                dateFormat="dd/MM/yyyy"
                isClearable
                placeholderText="Select date range"
                maxDate={new Date()}
                showDisabledMonthNavigation
              />
              {(dateRange.startDate || dateRange.endDate) && (
                <button
                  onClick={() => setDateRange({ startDate: null, endDate: null })}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <DataTable
            data={filteredExpenses}
            columns={columns}
            sorting={sorting}
            setSorting={setSorting}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            loading={loading}
            showSearch={false}
          />
        </div>
      </div>

      {/* FAB and Modal */}
      <button
        onClick={() => setShowAddModal(true)}
        className="fixed lg:bottom-6 bottom-20 right-6 p-4 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 z-40
          flex items-center justify-center group"
      >
        <PlusIcon className="h-6 w-6" />
        <span className="hidden md:block ml-2 mr-1 group-hover:mr-2 transition-all duration-200">
          Add Expense
        </span>
      </button>

      <AddExpenseModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        vehicles={vehicles}
        onSubmit={handleAddExpense}
      />
    </div>
  );
}